import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { Loader2, CheckCircle, Key, Mail, LogOut, Download, MonitorSmartphone } from 'lucide-react';
import AuthForm from '../components/AuthForm';
import { useAuth } from '../hooks/useAuth';
import { getIdToken, signOut, resendVerificationEmail } from '../lib/auth';
import { setTier } from '../lib/license';
import { bindPendingCode } from '../lib/bindPendingCode';
import { isDesktop } from '../lib/platform';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

// Latest signed + notarized Mac DMG. Hosted on a public GCS bucket
// (scrivar-downloads) because the pound-pixel GitHub repo is private —
// anonymous GH Release URLs return 404 to non-authenticated visitors.
// These point at STABLE filenames in releases/latest/ that every release
// overwrites, so the constants (and the matching hrefs in web/download.html)
// never need bumping again. Mac ships PER-ARCH DMGs (engines are bundled and
// arch-specific): release step copies the versioned DMG over
// gs://scrivar-downloads/releases/latest/Scrivar-<arch>.dmg (short
// Cache-Control). The legacy Scrivar-universal.dmg stable name is frozen at
// the last universal build (1.0.26) for old cached pages.
const MAC_ARM64_DOWNLOAD_URL =
  'https://storage.googleapis.com/scrivar-downloads/releases/latest/Scrivar-arm64.dmg';
const MAC_X64_DOWNLOAD_URL =
  'https://storage.googleapis.com/scrivar-downloads/releases/latest/Scrivar-x64.dmg';

// Latest EV-signed Windows installer. Same stable-filename scheme as the DMG —
// every release overwrites releases/latest/Scrivar-Setup.exe, so this constant
// never needs bumping.
const WIN_DOWNLOAD_URL =
  'https://storage.googleapis.com/scrivar-downloads/releases/latest/Scrivar-Setup.exe';

/**
 * /activate — single-page three-state flow.
 *
 *   1. Signed out         → embedded AuthForm (Create account tab default).
 *   2. Signed in, unverified → "Check your email" screen with resend + auto-poll.
 *   3. Signed in, verified  → code entry form.
 *
 * Firebase auto-signs-in users immediately after signUp, so we MUST gate on
 * emailVerified or the verify screen gets skipped. The override lets the
 * verify screen confirm via user.reload() and advance to the code form
 * without forcing useAuth to re-emit.
 */
export default function Activate() {
  const { user, loading, refreshAccount } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifiedOverride, setVerifiedOverride] = useState(false);
  const [autoActivated, setAutoActivated] = useState(false);

  // A desktop signup's verification email lands here in the BROWSER (the app
  // session lives in the app, not this tab). The app is already polling and
  // binds the code automatically, so there's nothing to do here — tell them to
  // go back to the app rather than dropping them into the web sign-in flow.
  if (searchParams.get('from') === 'app') {
    return (
      <Page subtitle="">
        <ReturnToAppScreen />
      </Page>
    );
  }

  if (loading) {
    return (
      <Page subtitle="">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" aria-label="Loading" />
        </div>
      </Page>
    );
  }

  if (!user) {
    return (
      <Page subtitle="Create an account or sign in to activate your code.">
        <AuthForm defaultTab="signup" />
      </Page>
    );
  }

  if (autoActivated) {
    return (
      <Page subtitle="">
        <PostActivationDownload />
      </Page>
    );
  }

  if (!user.emailVerified && !verifiedOverride) {
    return (
      <Page subtitle="One more step — verify your email to activate.">
        <VerifyEmailScreen
          user={user}
          onVerified={async () => {
            // Auto-bind a code entered at signup (stashed in pendingCode) so the
            // user doesn't have to re-type it. bindPendingCode force-refreshes
            // the token (the email_verified claim lags out-of-band verification)
            // and clears the code on success / permanent rejection.
            const { bound } = await bindPendingCode();
            // Refresh so useAuth.account picks up the new tier + email_verified.
            await refreshAccount({ forceTokenRefresh: true });
            if (bound) {
              // In the desktop app, the "download the app" success screen is
              // nonsense — send them to the Welcome/Continue screen instead.
              if (isDesktop()) { navigate('/onboarding', { replace: true }); return; }
              setAutoActivated(true);
              return;
            }
            // No code, or bind didn't take — fall through to the manual form.
            setVerifiedOverride(true);
          }}
        />
      </Page>
    );
  }

  return (
    <Page subtitle="Enter the registration code from your card or email.">
      <CodeEntryForm refreshAccount={refreshAccount} />
    </Page>
  );
}

function Page({ subtitle, children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 sm:py-12 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6 sm:mb-8 text-center">
          <div
            className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center text-white font-extrabold text-xl shadow-sm select-none"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
            aria-hidden="true"
          >
            S
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Activate Scrivar</h1>
          {subtitle && <p className="text-sm text-gray-500 px-4">{subtitle}</p>}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}

function VerifyEmailScreen({ user, onVerified }) {
  const [checking, setChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);

  const recheck = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setChecking(true);
      setError(null);
      setStatusMsg(null);
    }
    try {
      await user.reload();
      if (user.emailVerified) {
        onVerified();
      } else if (!silent) {
        setStatusMsg("Still not verified. Click the link in your email, then try again.");
      }
    } catch {
      if (!silent) setError('Could not check verification status. Please try again.');
    } finally {
      if (!silent) setChecking(false);
    }
  }, [user, onVerified]);

  // Auto-poll every 5 s so the user doesn't have to click anything once they
  // click the verification link in another tab.
  useEffect(() => {
    const interval = setInterval(() => recheck({ silent: true }), 5000);
    return () => clearInterval(interval);
  }, [recheck]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function handleResend() {
    if (resendCooldown > 0) return;
    setError(null);
    setStatusMsg(null);
    try {
      await resendVerificationEmail();
      setResendCooldown(30);
      setStatusMsg('Verification email resent. Check your inbox.');
    } catch (err) {
      console.error('[activate] resend verification failed:', err?.code, err?.message);
      setError(
        err?.code === 'auth/too-many-requests'
          ? 'Too many attempts. Please wait a minute, then try again.'
          : 'Could not resend verification email. Please try again.'
      );
    }
  }

  async function handleSignOut() {
    await signOut().catch(() => {});
  }

  return (
    <div className="p-6 text-center">
      <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4">
        <Mail className="w-6 h-6 text-indigo-500" aria-hidden="true" />
      </div>
      <h2 className="text-base font-semibold text-gray-900 mb-2">Check your email</h2>
      <p className="text-sm text-gray-600 mb-6">
        We sent a verification link to{' '}
        <span className="font-medium text-gray-800 break-all">{user.email}</span>.
        Click the link, then come back here.
      </p>

      {(error || statusMsg) && (
        <p
          role={error ? 'alert' : 'status'}
          className={`mb-4 text-sm rounded-lg px-3 py-2 ${
            error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'
          }`}
        >
          {error || statusMsg}
        </p>
      )}

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => recheck()}
          disabled={checking}
          className="w-full px-4 py-3 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
        >
          {checking ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Checking…
            </>
          ) : (
            "I've verified my email"
          )}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend verification email'}
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          className="w-full px-4 py-2 text-xs text-gray-500 hover:text-gray-700 inline-flex items-center justify-center gap-1.5"
        >
          <LogOut className="w-3 h-3" aria-hidden="true" />
          Sign out and use a different email
        </button>
      </div>
    </div>
  );
}

function CodeEntryForm({ refreshAccount }) {
  // On WEB we deliberately don't auto-redirect to /tools on success — the whole
  // reason a customer scanned the Amazon card and signed in was to download the
  // desktop app, so we show PostActivationDownload. On DESKTOP we redirect to
  // the Welcome screen instead (navigate, below).
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  function handleCodeChange(e) {
    let val = e.target.value.toUpperCase().replace(/[^23456789ABCDEFGHJKMNPQRSTUVWXYZ-]/g, '');
    const raw = val.replace(/-/g, '').replace(/^SCRV/, '');
    if (raw.length <= 12) {
      const parts = ['SCRV'];
      for (let i = 0; i < raw.length; i += 4) {
        parts.push(raw.slice(i, i + 4));
      }
      val = parts.join('-');
    }
    setCode(val);
  }

  async function handleActivate(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const normalizedCode = code.toUpperCase().trim();
      const token = await getIdToken();
      const response = await fetch(`${API_URL}/account/link-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: normalizedCode }),
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.message || 'Activation failed. Please check your code and try again.');
        return;
      }
      setTier(data.tier);
      // Re-fetch /account so useAuth.account picks up the new effective tier
      // (lifetime_lite/lifetime_pro). Without this, the user lands on /tools
      // or /account with the stale free-tier account row from sign-in and
      // sees "Upgrade to Lite" upsells despite just activating.
      if (refreshAccount) {
        try { await refreshAccount(); } catch { /* non-fatal */ }
      }
      // In the desktop app, skip the "download the app" screen (the user is
      // already in it) and go to the Welcome/Continue screen.
      if (isDesktop()) { navigate('/onboarding', { replace: true }); return; }
      setSuccess(true);
    } catch {
      setError('Could not connect to the activation server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return <PostActivationDownload />;
  }

  return (
    <form onSubmit={handleActivate} className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Key className="w-4 h-4 text-brand-blue" aria-hidden="true" />
        <label htmlFor="activate-code" className="text-sm font-medium text-gray-800">
          Registration code
        </label>
      </div>

      <input
        id="activate-code"
        type="text"
        value={code}
        onChange={handleCodeChange}
        placeholder="SCRV-XXXX-XXXX-XXXX"
        className="w-full px-4 py-3 border rounded-lg text-center font-mono text-lg tracking-normal sm:tracking-wider focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
        maxLength={19}
        autoFocus
        autoComplete="off"
        spellCheck="false"
        disabled={loading}
      />

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || code.length < 19}
        className="w-full mt-4 px-4 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Activating…
          </>
        ) : (
          'Activate'
        )}
      </button>
    </form>
  );
}

/**
 * Shown when a DESKTOP signup's verification email link lands on the web
 * /activate?from=app (the email link opens in the system browser, not the app).
 * The desktop app is already polling for verification and binds the code
 * automatically, so the browser tab has nothing to do — we just point the user
 * back to the app instead of dropping them into the web sign-in/code flow.
 *
 * We intentionally don't deep-link via pdfsuite:// here: it would add a fragile
 * "Open Scrivar?" OS prompt, and the app finishes on its own regardless.
 */
function ReturnToAppScreen() {
  return (
    <div className="p-6 text-center">
      <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-7 h-7 text-green-500" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Email verified</h2>
      <p className="text-sm text-gray-600 mb-6">
        You&rsquo;re all set. Head back to the{' '}
        <span className="font-medium text-gray-800">Scrivar app</span> — it finishes activating
        automatically. You can close this tab.
      </p>
      <div className="flex items-center justify-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3">
        <MonitorSmartphone className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
        Return to the Scrivar window you already have open.
      </div>
      <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-500">
        Prefer the browser?{' '}
        <Link to="/tools" className="text-brand-blue hover:underline">Use Scrivar on the web</Link>.
      </div>
    </div>
  );
}

/**
 * Post-activation screen. Reached after CodeEntryForm successfully binds
 * the registration code to the account. The user came in from an Amazon
 * card so the primary CTA is "download the desktop app." Both Mac (signed +
 * notarized DMG) and Windows (EV-signed installer) builds are live, served
 * from stable filenames in releases/latest/.
 *
 * Escape hatch: a small link to /tools for users who want to use the
 * web app immediately instead of waiting on a 200 MB download.
 */
function PostActivationDownload() {
  return (
    <div className="p-6 text-center">
      <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-7 h-7 text-green-500" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Activated!</h2>
      <p className="text-sm text-gray-600 mb-6">
        Your account is licensed. Download Scrivar to get started on your computer.
      </p>

      <div className="flex flex-col gap-3">
        {/* Mac — per-arch builds (engines are bundled + arch-specific) */}
        <a
          href={MAC_ARM64_DOWNLOAD_URL}
          className="w-full px-4 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark inline-flex items-center justify-center gap-2 min-h-[44px]"
          aria-label="Download Scrivar for Mac (Apple Silicon)"
        >
          <svg width="16" height="18" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M13.5 10.6c-.02-2.04 1.67-3.02 1.75-3.07C14.2 5.66 12.3 5.4 11.62 5.38c-1.36-.14-2.68.8-3.38.8-.7 0-1.77-.79-2.91-.76C3.85 5.44 2.2 6.47 1.3 8.08c-1.84 3.19-.47 7.91 1.31 10.5.87 1.27 1.91 2.7 3.27 2.65 1.32-.06 1.81-.85 3.4-.85 1.58 0 2.03.85 3.42.82 1.41-.02 2.31-1.29 3.17-2.56.99-1.44 1.4-2.85 1.43-2.92C17.28 15.7 13.52 14.27 13.5 10.6z" fill="currentColor" />
            <path d="M10.66 3.57c.73-.89 1.22-2.11 1.08-3.34-1.05.04-2.3.7-3.06 1.58-.67.77-1.25 2.01-1.09 3.2 1.17.09 2.34-.59 3.07-1.44z" fill="currentColor" />
          </svg>
          Download for Mac · Apple Silicon
        </a>

        <a
          href={MAC_X64_DOWNLOAD_URL}
          className="w-full px-4 py-3 border border-gray-300 text-gray-800 rounded-lg font-medium hover:bg-gray-50 inline-flex items-center justify-center gap-2 min-h-[44px]"
          aria-label="Download Scrivar for Mac (Intel)"
        >
          <svg width="16" height="18" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M13.5 10.6c-.02-2.04 1.67-3.02 1.75-3.07C14.2 5.66 12.3 5.4 11.62 5.38c-1.36-.14-2.68.8-3.38.8-.7 0-1.77-.79-2.91-.76C3.85 5.44 2.2 6.47 1.3 8.08c-1.84 3.19-.47 7.91 1.31 10.5.87 1.27 1.91 2.7 3.27 2.65 1.32-.06 1.81-.85 3.4-.85 1.58 0 2.03.85 3.42.82 1.41-.02 2.31-1.29 3.17-2.56.99-1.44 1.4-2.85 1.43-2.92C17.28 15.7 13.52 14.27 13.5 10.6z" fill="currentColor" />
            <path d="M10.66 3.57c.73-.89 1.22-2.11 1.08-3.34-1.05.04-2.3.7-3.06 1.58-.67.77-1.25 2.01-1.09 3.2 1.17.09 2.34-.59 3.07-1.44z" fill="currentColor" />
          </svg>
          Download for Mac · Intel
        </a>

        {/* Windows — EV-signed installer is live (releases/latest/Scrivar-Setup.exe). */}
        <a
          href={WIN_DOWNLOAD_URL}
          className="w-full px-4 py-3 border border-gray-300 text-gray-800 rounded-lg font-medium hover:bg-gray-50 inline-flex items-center justify-center gap-2 min-h-[44px]"
          aria-label="Download Scrivar for Windows"
        >
          <Download className="w-4 h-4" aria-hidden="true" />
          Download for Windows
        </a>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-500">
        Prefer the browser?{' '}
        <Link to="/tools" className="text-brand-blue hover:underline">
          Open the web app
        </Link>
        .
      </div>

      <p className="mt-4 text-xs text-gray-400">
        macOS 11 or later · Windows 10 or later
      </p>
    </div>
  );
}
